import {
	type Cell,
	type Column,
	flexRender,
	type Row
} from '@tanstack/react-table'
import type { VirtualItem } from '@tanstack/react-virtual'
import {
	type CSSProperties,
	type MouseEvent,
	type ReactNode,
	type RefObject,
	useEffect,
	useRef
} from 'react'
import { useVirtualization } from '#hooks/use-virtualization'
import { Checkbox } from '#ui/checkbox'
import { GridBody, GridCell } from '#ui/grid'
import { Skeleton } from '#ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from '#ui/tooltip'
import {
	DATA_TABLE_SURFACE_WIDTH,
	getColumnBgClassName,
	getColumnPinStyles,
	getColumnWidthVar,
	ROW_STATE_BG
} from './pin-styles'
import { type DataTableLoadMoreConfig, SELECT_COLUMN_ID } from './types'

const COLUMN_VIRTUALIZATION_THRESHOLD = 50

interface DataTableBodySelection<TData> {
	selectedIds: Set<string>
	isRowSelectable?: (row: TData) => boolean
	notSelectableTooltip?: ReactNode | ((row: TData) => ReactNode)
	onToggleRow: (rowId: string, checked: boolean, shiftKey: boolean) => void
	rowAriaLabel: string
}

interface SelectCellInfo {
	rowId: string
	isSelected: boolean
	selectable: boolean
	disabledTooltip: ReactNode
	ariaLabel: string
	onToggle: (rowId: string, checked: boolean, shiftKey: boolean) => void
}

interface DataTableBodyProps<TData> {
	rows: Row<TData>[]
	columnsKey: string
	isLoading: boolean | undefined
	loadingRows: number
	loadingState: ReactNode
	leafColumns: Column<TData, unknown>[]
	rowHeight: number
	overscan: number
	loaderText: string
	headerRowCount: number
	loadMore: DataTableLoadMoreConfig | undefined
	pageIndex: number | undefined
	scrollRef: RefObject<HTMLDivElement | null>
	selection: DataTableBodySelection<TData> | undefined
	onRowClick: ((row: TData) => void) | undefined
}

export function DataTableBody<TData>(props: DataTableBodyProps<TData>) {
	const virtualizeColumns =
		props.leafColumns.length > COLUMN_VIRTUALIZATION_THRESHOLD
	const rowCount =
		props.isLoading === true
			? 0
			: props.rows.length + Number(props.loadMore?.loading === true)
	const onEndReached =
		props.isLoading ||
		props.loadMore?.hasMore === false ||
		props.loadMore?.loading
			? undefined
			: props.loadMore?.onLoadMore
	const virtual = useVirtualization<HTMLDivElement>({
		scrollRef: props.scrollRef,
		vertical: {
			count: rowCount,
			estimateSize: () => props.rowHeight,
			overscan: props.overscan,
			getItemKey: (index) => {
				if (index >= props.rows.length) {
					return '__loader__'
				}
				return props.rows[index].id
			},
			threshold: props.loadMore?.threshold,
			onEndReached
		},
		horizontal: virtualizeColumns
			? {
					count: props.leafColumns.length,
					estimateSize: (index) =>
						props.leafColumns[index]?.getSize() ?? 160,
					getItemKey: (index) => props.leafColumns[index]?.id ?? index
				}
			: undefined
	})
	const totalColumnsWidth = virtualizeColumns
		? virtual.horizontal.totalSize
		: DATA_TABLE_SURFACE_WIDTH

	const prevPageIndexRef = useRef(props.pageIndex)
	useEffect(() => {
		if (props.pageIndex === undefined) {
			prevPageIndexRef.current = props.pageIndex
			return
		}
		if (prevPageIndexRef.current === props.pageIndex) {
			return
		}
		prevPageIndexRef.current = props.pageIndex
		virtual.vertical.virtualizer.scrollToOffset(0)
	}, [props.pageIndex, virtual.vertical.virtualizer])

	if (props.isLoading) {
		if (props.loadingState !== undefined) {
			return <>{props.loadingState}</>
		}
		return (
			<DataTableSkeletonBody
				headerRowCount={props.headerRowCount}
				leafColumns={props.leafColumns}
				loadingRows={props.loadingRows}
				rowHeight={props.rowHeight}
			/>
		)
	}

	return (
		<GridBody
			className='relative'
			data-table-width='true'
			style={{
				height: virtual.vertical.totalSize,
				width: totalColumnsWidth
			}}
		>
			{virtual.vertical.items.map((virtualRow) => {
				// When the rows shrink, the virtualizer still lists the old count for one render.
				if (virtualRow.index >= rowCount) {
					return null
				}
				if (virtualRow.index >= props.rows.length) {
					return (
						<DataTableLoaderRow
							ariaRowIndex={
								props.headerRowCount + props.rows.length + 1
							}
							key='__loader__'
							loaderText={props.loaderText}
							virtualSize={virtualRow.size}
							virtualStart={virtualRow.start}
						/>
					)
				}
				const row = props.rows[virtualRow.index]
				const selection = props.selection
				const selectable =
					selection?.isRowSelectable?.(row.original) ?? true
				const tooltip =
					selection && !selectable
						? selection.notSelectableTooltip
						: undefined
				return (
					<DataTableRow
						ariaRowIndex={
							props.headerRowCount + virtualRow.index + 1
						}
						columnsKey={props.columnsKey}
						key={row.id}
						onRowClick={props.onRowClick}
						onToggleRow={selection?.onToggleRow}
						row={row}
						selectAriaLabel={selection?.rowAriaLabel}
						selectable={selectable}
						selectDisabledTooltip={
							typeof tooltip === 'function'
								? tooltip(row.original)
								: tooltip
						}
						selectedIds={selection?.selectedIds}
						totalColumnsWidth={totalColumnsWidth}
						virtualColumns={
							virtualizeColumns
								? virtual.horizontal.items
								: undefined
						}
						virtualIndex={virtualRow.index}
						virtualSize={virtualRow.size}
						virtualStart={virtualRow.start}
					/>
				)
			})}
		</GridBody>
	)
}

interface DataTableRowProps<TData> {
	row: Row<TData>
	columnsKey: string
	virtualColumns: VirtualItem[] | undefined
	virtualIndex: number
	virtualStart: number
	virtualSize: number
	ariaRowIndex: number
	totalColumnsWidth: CSSProperties['width']
	selectedIds: Set<string> | undefined
	selectable: boolean
	selectDisabledTooltip: ReactNode
	onToggleRow:
		| ((rowId: string, checked: boolean, shiftKey: boolean) => void)
		| undefined
	selectAriaLabel: string | undefined
	onRowClick: ((row: TData) => void) | undefined
}

/**
 * A click on the row opens it, unless it lands on something that does its own job: a link,
 * a button or a field in a cell, or anything rendered in a portal (a dialog opened from a
 * cell still bubbles to the row through the React tree, not through the DOM).
 */
const ROW_CLICK_IGNORED =
	'a, button, input, label, [role="checkbox"], [role="menuitem"]'

function isRowOwnClick(event: MouseEvent<HTMLDivElement>) {
	const target = event.target
	if (!(target instanceof Element && event.currentTarget.contains(target))) {
		return false
	}
	return (
		target.closest(ROW_CLICK_IGNORED) === null &&
		window.getSelection()?.isCollapsed !== false
	)
}

/**
 * Cells depend on the visible columns; `columnsKey` is taken as an input so the
 * memoized call site refreshes them when columns change (TanStack caches the row
 * model on `data` alone, so the row reference stays stable across column changes).
 */
function getRowVisibleCells<TData>(row: Row<TData>, columnsKey: string) {
	return columnsKey === '' ? [] : row.getVisibleCells()
}

function DataTableRow<TData>(props: DataTableRowProps<TData>) {
	return (
		// biome-ignore lint/a11y/useFocusableInteractive: DataTable uses div-based ARIA table rows for virtualization, matching the shared grid primitive.
		// biome-ignore lint/a11y/useSemanticElements: Native table rows cannot be absolutely positioned for the virtualized body.
		// biome-ignore lint/a11y/useKeyWithClickEvents: The row click is a pointer shortcut; keyboard users reach the same destination through the link kept in a cell.
		<div
			aria-rowindex={props.ariaRowIndex}
			className={
				props.onRowClick ? 'group flex cursor-pointer' : 'group flex'
			}
			data-index={props.virtualIndex}
			data-slot='grid-row'
			data-table-width='true'
			onClick={
				props.onRowClick
					? (event) => {
							if (isRowOwnClick(event)) {
								props.onRowClick?.(props.row.original)
							}
						}
					: undefined
			}
			role='row'
			style={{
				position: 'absolute',
				top: 0,
				left: 0,
				transform: `translateY(${props.virtualStart}px)`,
				width: props.totalColumnsWidth,
				height: props.virtualSize
			}}
		>
			{renderVirtualCells({
				cells: getRowVisibleCells(props.row, props.columnsKey),
				totalColumnsWidth: props.totalColumnsWidth,
				virtualColumns: props.virtualColumns,
				selectCell: props.onToggleRow
					? {
							rowId: props.row.id,
							isSelected:
								props.selectedIds?.has(props.row.id) ?? false,
							selectable: props.selectable,
							disabledTooltip: props.selectDisabledTooltip,
							ariaLabel: props.selectAriaLabel ?? '',
							onToggle: props.onToggleRow
						}
					: undefined
			})}
		</div>
	)
}

function renderVirtualCells<TData>(props: {
	cells: Cell<TData, unknown>[]
	virtualColumns: VirtualItem[] | undefined
	totalColumnsWidth: CSSProperties['width']
	selectCell: SelectCellInfo | undefined
}) {
	const renderCell = (cell: Cell<TData, unknown>) => {
		const ariaColIndex = cell.column.getIndex() + 1
		if (props.selectCell && cell.column.id === SELECT_COLUMN_ID) {
			return (
				<DataTableSelectCell
					ariaColIndex={ariaColIndex}
					column={cell.column}
					key={cell.id}
					selection={props.selectCell}
				/>
			)
		}
		return (
			<DataTableCell
				ariaColIndex={ariaColIndex}
				cell={cell}
				key={cell.id}
			/>
		)
	}
	/** Only center cells are virtualized; pinned cells must always stay mounted. */
	const leftCells = props.cells.filter(
		(cell) => cell.column.getIsPinned() === 'left'
	)
	const rightCells = props.cells.filter(
		(cell) => cell.column.getIsPinned() === 'right'
	)
	const virtualIndexes =
		props.virtualColumns === undefined
			? undefined
			: new Set(props.virtualColumns.map((column) => column.index))
	const centerCells = props.cells.filter(
		(cell, index) =>
			cell.column.getIsPinned() === false &&
			(virtualIndexes === undefined || virtualIndexes.has(index))
	)
	if (props.virtualColumns === undefined) {
		return (
			<>
				{leftCells.map(renderCell)}
				{centerCells.map(renderCell)}
				{rightCells.length > 0 && (
					<div
						aria-hidden='true'
						className={`min-w-0 flex-1 border-b ${ROW_STATE_BG}`}
					/>
				)}
				{rightCells.map(renderCell)}
			</>
		)
	}
	const leftWidth = leftCells.reduce(
		(total, cell) => total + cell.column.getSize(),
		0
	)
	const rightWidth = rightCells.reduce(
		(total, cell) => total + cell.column.getSize(),
		0
	)
	const firstCenter = props.virtualColumns.find(
		(column) => props.cells[column.index]?.column.getIsPinned() === false
	)
	const lastCenter = props.virtualColumns.findLast(
		(column) => props.cells[column.index]?.column.getIsPinned() === false
	)
	/** Spacers keep the full horizontal scroll width while center cells are windowed. */
	const beforeCenter = Math.max(
		0,
		(firstCenter?.start ?? leftWidth) - leftWidth
	)
	const afterCenter = Math.max(
		0,
		Number(props.totalColumnsWidth) -
			rightWidth -
			((lastCenter?.start ?? leftWidth) + (lastCenter?.size ?? 0))
	)

	return (
		<>
			{leftCells.map(renderCell)}
			{beforeCenter > 0 && (
				<div style={{ flexShrink: 0, width: beforeCenter }} />
			)}
			{centerCells.map(renderCell)}
			{afterCenter > 0 && (
				<div style={{ flexShrink: 0, width: afterCenter }} />
			)}
			{rightCells.map(renderCell)}
		</>
	)
}

function DataTableSelectCell<TData>(props: {
	column: Column<TData, unknown>
	ariaColIndex: number
	selection: SelectCellInfo
}) {
	const checkbox = (
		<Checkbox
			aria-label={props.selection.ariaLabel}
			checked={props.selection.isSelected}
			disabled={!props.selection.selectable}
			onCheckedChange={(checked, details) => {
				const shiftKey =
					details.event instanceof MouseEvent &&
					details.event.shiftKey
				props.selection.onToggle(
					props.selection.rowId,
					checked,
					shiftKey
				)
			}}
		/>
	)
	const showTooltip =
		!props.selection.selectable && props.selection.disabledTooltip != null
	return (
		<GridCell
			aria-colindex={props.ariaColIndex}
			className={`justify-center border-b ${getColumnBgClassName(props.column)}`}
			data-column-id={props.column.id}
			style={{
				width: getColumnWidthVar(props.column.id),
				...getColumnPinStyles(props.column)
			}}
		>
			{showTooltip ? (
				<TooltipProvider delay={300}>
					<Tooltip>
						{/* Disabled checkboxes don't emit hover; the span trigger does. */}
						<TooltipTrigger
							render={<span className='inline-flex' />}
						>
							{checkbox}
						</TooltipTrigger>
						<TooltipContent>
							{props.selection.disabledTooltip}
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : (
				checkbox
			)}
		</GridCell>
	)
}

function DataTableCell<TData>(props: {
	cell: Cell<TData, unknown>
	ariaColIndex: number
}) {
	return (
		<GridCell
			aria-colindex={props.ariaColIndex}
			className={`border-b ${getColumnBgClassName(props.cell.column)}`}
			data-column-id={props.cell.column.id}
			style={{
				width: getColumnWidthVar(props.cell.column.id),
				...getColumnPinStyles(props.cell.column)
			}}
		>
			<div className='min-w-0 flex-1 truncate'>
				{flexRender(
					props.cell.column.columnDef.cell,
					props.cell.getContext()
				)}
			</div>
		</GridCell>
	)
}

function DataTableLoaderRow(props: {
	virtualStart: number
	virtualSize: number
	loaderText: string
	ariaRowIndex: number
}) {
	return (
		// biome-ignore lint/a11y/useFocusableInteractive: DataTable uses div-based ARIA table rows for virtualization, matching the shared grid primitive.
		// biome-ignore lint/a11y/useSemanticElements: Native table rows cannot be absolutely positioned for the virtualized body.
		<div
			aria-rowindex={props.ariaRowIndex}
			data-slot='grid-row'
			data-table-width='true'
			role='row'
			style={{
				position: 'absolute',
				top: 0,
				left: 0,
				transform: `translateY(${props.virtualStart}px)`,
				width: DATA_TABLE_SURFACE_WIDTH,
				height: props.virtualSize
			}}
		>
			<GridCell
				className='shrink-0 grow-0 justify-center border-b text-muted-foreground text-sm'
				style={{
					width: DATA_TABLE_SURFACE_WIDTH
				}}
			>
				{/* biome-ignore lint/a11y/useSemanticElements: `<output>` is a form-result element with wrong semantics here; this is a polite live region nested inside a table cell */}
				<div aria-live='polite' role='status'>
					{props.loaderText}
				</div>
			</GridCell>
		</div>
	)
}

function DataTableSkeletonBody<TData>(props: {
	loadingRows: number
	leafColumns: Column<TData, unknown>[]
	rowHeight: number
	headerRowCount: number
}) {
	const leftColumns = props.leafColumns.filter(
		(column) => column.getIsPinned() === 'left'
	)
	const centerColumns = props.leafColumns.filter(
		(column) => column.getIsPinned() === false
	)
	const rightColumns = props.leafColumns.filter(
		(column) => column.getIsPinned() === 'right'
	)
	const renderCell = (col: Column<TData, unknown>, columnIndex: number) => (
		<GridCell
			aria-colindex={columnIndex + 1}
			className={`border-b ${getColumnBgClassName(col)}`}
			data-column-id={col.id}
			key={col.id}
			style={{
				width: getColumnWidthVar(col.id),
				...getColumnPinStyles(col)
			}}
		>
			<Skeleton className='h-4 w-3/4' />
		</GridCell>
	)
	const skeletonRows = Array.from(
		{ length: props.loadingRows },
		(_, index) => `loading-${index}`
	)
	return (
		<GridBody
			data-table-width='true'
			style={{
				width: DATA_TABLE_SURFACE_WIDTH
			}}
		>
			{skeletonRows.map((rowId, index) => (
				// biome-ignore lint/a11y/useFocusableInteractive: DataTable uses div-based ARIA table rows for virtualization, matching the shared grid primitive.
				// biome-ignore lint/a11y/useSemanticElements: Native table rows cannot be absolutely positioned for the virtualized body.
				<div
					aria-rowindex={props.headerRowCount + index + 1}
					className='group flex'
					data-slot='grid-row'
					data-table-width='true'
					key={rowId}
					role='row'
					style={{
						width: DATA_TABLE_SURFACE_WIDTH,
						height: props.rowHeight
					}}
				>
					{leftColumns.map((col, index) => renderCell(col, index))}
					{centerColumns.map((col, index) =>
						renderCell(col, leftColumns.length + index)
					)}
					{rightColumns.length > 0 && (
						<div
							aria-hidden='true'
							className='min-w-0 flex-1 border-b'
						/>
					)}
					{rightColumns.map((col, index) =>
						renderCell(
							col,
							leftColumns.length + centerColumns.length + index
						)
					)}
				</div>
			))}
		</GridBody>
	)
}
